import React from "react";
import { Box, Skeleton, Typography } from "@mui/material";
import {
	Area,
	AreaChart,
	CartesianGrid,
	ResponsiveContainer,
	Tooltip,
	TooltipProps,
	XAxis,
	YAxis,
} from "recharts";
import { useTranslation } from "react-i18next";
import { PROGRAM_DAYS, SOWS_MAX_SCORE } from "../../constants/program";

const DEFAULT_COLOR = "#1D9E75";

export interface SowsChartPoint {
	day: number;
	score: number;
}

interface SowsProgressionChartProps {
	title: string;
	data: SowsChartPoint[] | undefined;
	isLoading: boolean;
	color?: string;
	emptyMessage?: string;
}

interface ChartPoint {
	day: number;
	score: number | null;
}

const buildSeries = (data: SowsChartPoint[] | undefined): ChartPoint[] => {
	const byDay = new Map<number, number>();
	(data ?? []).forEach((p) => byDay.set(p.day, p.score));
	return Array.from({ length: PROGRAM_DAYS }, (_, i) => {
		const day = i + 1;
		const score = byDay.get(day);
		return { day, score: score === undefined ? null : score };
	});
};

const CustomTooltip: React.FC<TooltipProps<number, string>> = ({
	active,
	payload,
}) => {
	if (!active || !payload || payload.length === 0) {
		return null;
	}

	const point = payload[0].payload as ChartPoint;
	if (point.score === null) {
		return null;
	}

	return (
		<Box
			sx={{
				bgcolor: "background.paper",
				border: "1px solid",
				borderColor: "divider",
				borderRadius: 1,
				px: 1.5,
				py: 1,
				boxShadow: 2,
			}}
		>
			<Typography variant="body2">
				Day {point.day}: {point.score.toFixed(1)}
			</Typography>
		</Box>
	);
};

const SowsProgressionChart: React.FC<SowsProgressionChartProps> = ({
	title,
	data,
	isLoading,
	color = DEFAULT_COLOR,
	emptyMessage,
}) => {
	const { t } = useTranslation("dashboard");

	const series = buildSeries(data);
	const hasAnyData = series.some((p) => p.score !== null);
	const fallbackEmpty = emptyMessage ?? t("sowsNoDataAvailable");

	return (
		<Box
			sx={{
				bgcolor: "background.paper",
				borderRadius: 2,
				boxShadow: 1,
				p: 3,
				mb: 4,
			}}
		>
			<Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
				{title}
			</Typography>

			{isLoading ? (
				<Skeleton variant="rectangular" height={280} />
			) : !hasAnyData ? (
				<Box
					sx={{
						height: 280,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
					}}
				>
					<Typography variant="body2" color="textSecondary">
						{fallbackEmpty}
					</Typography>
				</Box>
			) : (
				<ResponsiveContainer width="100%" height={280}>
					<AreaChart
						data={series}
						margin={{ top: 16, right: 24, bottom: 0, left: 0 }}
					>
						<defs>
							<linearGradient
								id={`sowsFill-${color}`}
								x1="0"
								y1="0"
								x2="0"
								y2="1"
							>
								<stop offset="0%" stopColor={color} stopOpacity={0.35} />
								<stop offset="100%" stopColor={color} stopOpacity={0.05} />
							</linearGradient>
						</defs>
						<CartesianGrid strokeDasharray="3 3" vertical={false} />
						<XAxis
							dataKey="day"
							type="number"
							domain={[1, PROGRAM_DAYS]}
							ticks={Array.from({ length: PROGRAM_DAYS }, (_, i) => i + 1)}
							allowDecimals={false}
						/>
						<YAxis
							domain={[0, SOWS_MAX_SCORE]}
							ticks={[0, 16, 32, 48, SOWS_MAX_SCORE]}
							allowDecimals={false}
						/>
						<Tooltip content={<CustomTooltip />} />
						<Area
							type="monotone"
							dataKey="score"
							stroke={color}
							strokeWidth={2}
							fill={`url(#sowsFill-${color})`}
							connectNulls={false}
							dot={{ r: 4, fill: color }}
							activeDot={{ r: 6 }}
							isAnimationActive={false}
						/>
					</AreaChart>
				</ResponsiveContainer>
			)}
		</Box>
	);
};

export default SowsProgressionChart;
